import { action, observable, runInAction, makeObservable } from 'mobx';
import { eventBus, subscribe } from 'mobx-event-bus2';

import { AxiosError } from 'axios';

import API from 'utils/api';
import { EventType } from 'utils/events/constants';
import Logger from 'utils/logger';
import LocationDetails from 'utils/stores/LocationDetails';

import { isNotFound } from 'utils/api/errors';

import { UserDetailsResponse } from 'utils/api/modules/user/user.types';

import RootStore from '../Root';
import UserDetailsData from './UserDetailsData';

export default class UserDetailsStore {
  store: RootStore;

  api: typeof API;

  @observable isLoaded = false;

  @observable error?: AxiosError;

  @observable details?: UserDetailsData;

  @observable showSafety?: boolean;

  locationDetails: LocationDetails;

  constructor(rootStore: RootStore, api: typeof API) {
    makeObservable(this);
    this.store = rootStore;
    this.api = api;

    this.locationDetails = new LocationDetails(this.store, this.api);

    eventBus.register(this);
  }

  @action.bound
  async loadDetails(userId: string, managersScope = false): Promise<void> {
    this.isLoaded = false;
    try {
      const { data } = managersScope
        ? await this.api.user.loadUserDataForManagers(userId)()
        : await this.api.user.loadUserData(userId)();

      runInAction(() => {
        this.error = undefined;
        this.details = new UserDetailsData(this.store, this.api, data as UserDetailsResponse);
        this.showSafety = data.showSafety;
      });
    } catch (e) {
      const err = e as AxiosError;
      runInAction(() => {
        this.error = err;
      });
      if (!isNotFound(err)) {
        Logger.error(`Invalid load user details API response. User Id: ${userId}`, err);
      }
    } finally {
      runInAction(() => {
        this.isLoaded = true;
      });
    }
  }

  @action.bound
  setHomeUnit(unitUuid: string): void {
    if (this.details) this.details.homeUnit = unitUuid;
  }

  @subscribe(EventType.LoggedOut)
  @action
  reset(): void {
    this.isLoaded = false;
    this.error = undefined;

    if (this.details) {
      this.details.reset();
      eventBus.unregister(this.details);
      this.details = undefined;
    }
  }

  @action
  async deleteUser(userId: string): Promise<boolean> {
    try {
      const data = { users: [userId] };
      await this.api.user.deleteUsers(data)();
      this.store.licenses.reset();
      this.store.notification.enqueueSuccessSnackbar('User deactivated successfully');
      return true;
    } catch (e) {
      Logger.error('Invalid deactivated user API response.', e);
      const errorMessage =
        (e as AxiosError<{ users: string[] }>).response?.data?.users?.[0] || 'Failed to deactivate user';
      this.store.notification.enqueueErrorSnackbar(errorMessage);
      return false;
    }
  }

  @action
  async reactivateUser(userId: string): Promise<boolean> {
    try {
      await this.api.user.reactivateUser(userId)();
      this.details?.setActive(true);
      this.store.notification.enqueueSuccessSnackbar('User reactivated successfully');
      return true;
    } catch (e) {
      Logger.error('Invalid reactivate user API response.', e);
      const errorMessage = (e as AxiosError<{ detail: string }>).response?.data?.detail || 'Failed to reactivate user';
      this.store.notification.enqueueErrorSnackbar(errorMessage);
      return false;
    }
  }

  @action
  async sendPasswordReset(userId: string, email: string): Promise<boolean> {
    try {
      await this.api.user.sendPasswordReset(userId)();
      this.store.notification.enqueueSuccessSnackbar(`Password reset email sent to ${email}`);
      return true;
    } catch (e) {
      Logger.error('Failed to send password reset email.', e);
      this.store.notification.enqueueErrorSnackbar('Failed to send password reset email');
      return false;
    }
  }

  @action
  async resendInvite(userId: string, email: string): Promise<boolean> {
    try {
      await this.api.user.resendInvite(userId)();
      this.store.notification.enqueueSuccessSnackbar(`Invitation resent to ${email}`);
      return true;
    } catch (e) {
      Logger.error('Failed to resend invitation email.', e);
      this.store.notification.enqueueErrorSnackbar('Failed to resend invitation email');
      return false;
    }
  }
}
